import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw,
  Search,
  Users,
  UserPlus,
  Eye,
  Filter,
  CheckCircle,
  XCircle,
  Mail,
  Award,
  Trash2,
  Loader2,
} from "lucide-react";

import { PERMS } from "../../../utils/perms";
import { useAccess } from "../../../utils/useAccess";
import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";

import {
  useGetAssessorsQuery,
  useDeleteAssessorMutation,
} from "../api/assessorsApi";

/**
 * Helper to format names consistently
 */
const fullName = (m: any) =>
  [m?.surname, m?.firstname, m?.othernames].filter(Boolean).join(" ") || "—";

/**
 * Reusable Stat Card matching your preferred pattern
 */
function StatCard({
  title,
  value,
  icon: Icon,
  colorClass,
}: {
  title: string;
  value: string | number;
  icon: any;
  colorClass: string;
}) {
  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-gray-600">{title}</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">
            {value}
          </div>
        </div>
        <div className={`p-3 rounded-lg ${colorClass}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function AssessorsListPage() {
  const { user, can } = useAccess();

  // UI State for filtering
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const [deleteAssessor] = useDeleteAssessorMutation();
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleRemove = async (a: any) => {
    const name = fullName(a?.member);
    if (
      !window.confirm(
        `Remove ${name} as an assessor? This revokes their assessor access. Their membership is unaffected.`
      )
    )
      return;
    setRemovingId(a.id);
    try {
      await deleteAssessor(a.id).unwrap();
    } catch (e: any) {
      alert(e?.data?.message || "Could not remove this assessor.");
    } finally {
      setRemovingId(null);
    }
  };

  const {
    data = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetAssessorsQuery();

  // Filter logic matching the Admins pattern
  const filteredAssessors = useMemo(() => {
    const nq = q.trim().toLowerCase();

    return data.filter((a: any) => {
      const name = fullName(a.member).toLowerCase();
      const email = (a.member?.email ?? "").toLowerCase();
      const memberNo = (a.member?.membership_no ?? "").toLowerCase();

      const matchesSearch =
        !nq || name.includes(nq) || email.includes(nq) || memberNo.includes(nq);

      const isActive = a.active ? "active" : "inactive";
      const matchesStatus = statusFilter === "all" || isActive === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [data, q, statusFilter]);

  // Stats calculation
  const stats = useMemo(() => {
    const total = data.length;
    const active = data.filter((a: any) => a.active).length;
    const totalGraded = data.reduce(
      (acc: number, curr: any) => acc + (curr.assessed_logbooks?.length || 0),
      0
    );
    return { total, active, totalGraded };
  }, [data]);

  return (
    <PageGate perm={PERMS.assessors.access}>
      <div className="space-y-6">
        {/* Header Area */}
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-gray-50 border text-gray-700">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Assessors Registry
                </h1>
                <p className="text-sm text-gray-500">
                  Monitor officer workloads and grading progress
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm font-medium transition-colors"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
                />
                Refresh
              </button>

              <AccessGate perm={PERMS.assessors.create}>
                <Link
                  to="/assessors/create"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm font-medium transition-colors shadow-sm"
                >
                  <UserPlus className="w-4 h-4" />
                  Add Assessor
                </Link>
              </AccessGate>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-8">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, email, or membership number..."
                  className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                />
              </div>
            </div>

            <div className="md:col-span-4">
              <div className="flex gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="flex-1 border rounded-lg px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active Officers</option>
                  <option value="inactive">Inactive Officers</option>
                </select>
                <button
                  onClick={() => {
                    setQ("");
                    setStatusFilter("all");
                  }}
                  className="px-4 py-2.5 border rounded-lg hover:bg-gray-50 text-sm"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            title="Total Registered"
            value={stats.total}
            icon={Users}
            colorClass="bg-blue-50 text-blue-600"
          />
          <StatCard
            title="Active Personnel"
            value={stats.active}
            icon={CheckCircle}
            colorClass="bg-green-50 text-green-600"
          />
          <StatCard
            title="Total Files Graded"
            value={stats.totalGraded}
            icon={Award}
            colorClass="bg-purple-50 text-purple-600"
          />
        </div>

        {/* Main Table Card */}
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="py-20 text-center text-gray-500">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">Retrieving Assessor Data...</p>
            </div>
          ) : error ? (
            <div className="py-20 text-center text-red-500 font-medium">
              Failed to load assessor data. Please try again.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-gray-50 border-b text-gray-500 font-semibold uppercase text-[11px] tracking-wider">
                    <th className="py-4 px-6">Officer Details</th>
                    <th className="py-4 px-4">Member ID</th>
                    <th className="py-4 px-4">Status</th>
                    <th className="py-4 px-4 text-center">Workload</th>
                    <th className="py-4 px-4 text-center">Graded</th>

                    <AccessGate perm={PERMS.assessors.detailsAccess}>
                      <th className="py-4 px-6 text-right">Actions</th>
                    </AccessGate>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredAssessors.map((a: any) => (
                    <tr
                      key={a.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <div className="font-semibold text-gray-900">
                          {fullName(a.member)}
                        </div>
                        <div className="flex items-center gap-1 text-gray-500 text-xs mt-0.5">
                          <Mail className="w-3 h-3" /> {a.member?.email || "—"}
                        </div>
                      </td>
                      <td className="py-4 px-4 font-mono text-gray-600 text-xs">
                        {a.member?.membership_no || "—"}
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            a.active
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              a.active ? "bg-green-600" : "bg-gray-400"
                            }`}
                          ></span>
                          {a.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="text-gray-900 font-medium">
                          {a.assigned_logbooks?.length ?? 0}
                        </div>
                        <div className="text-[10px] text-gray-400 uppercase">
                          of {a.max_workload || "∞"}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="font-semibold text-blue-600">
                          {a.assessed_logbooks?.length ?? 0}
                        </span>
                      </td>

                      <AccessGate perm={PERMS.assessors.detailsAccess}>
                        <td className="py-4 px-6 text-right">
                          <div className="inline-flex items-center gap-2">
                            <Link
                              to={`/assessors/${a.id}`}
                              className="inline-flex items-center gap-2 px-3 py-1.5 border rounded-lg hover:bg-gray-50 text-xs font-medium text-gray-700 transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View Profile
                            </Link>
                            <button
                              onClick={() => handleRemove(a)}
                              disabled={removingId === a.id}
                              title="Remove assessor"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 text-xs font-medium transition-colors disabled:opacity-60"
                            >
                              {removingId === a.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="w-3.5 h-3.5" />
                              )}
                              Remove
                            </button>
                          </div>
                        </td>
                      </AccessGate>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredAssessors.length === 0 && (
                <div className="py-20 text-center">
                  <SearchX className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">
                    No assessors found matching your filters.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PageGate>
  );
}

// Missing icons helper
function SearchX(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m13.5 8.5-5 5" />
      <path d="m8.5 8.5 5 5" />
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
